import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ROLE_LABEL } from "@/lib/api";
import { Radio, FileText, Users, Shield, Activity } from "lucide-react";

export default function TransparencyLedger() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/transparency/ledger").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="max-w-[1400px] mx-auto px-6 py-16 font-mono text-white/50">Loading ledger...</div>;
  }

  const s = data.nation_stats;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono text-[#89b4fa]">/// 04 · PUBLIC TRANSPARENCY LEDGER</div>
          <h1 className="font-display text-5xl mt-3">Council activity · on the record</h1>
          <p className="text-white/60 mt-3 max-w-2xl">
            Aggregated, real-time accountability feed for the Chief &amp; Council of Kasabonika Lake First Nation. Every leader&apos;s published output is counted here — no bodies, no PII, just the numbers. Council term {data.council_term}.
          </p>
        </div>
        <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
          Generated {new Date(data.generated_at).toLocaleString()}
        </div>
      </div>

      {/* Nation stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-10">
        {[
          [Users, "#89b4fa", "Approved Members", s.approved_members],
          [Shield, "#ef5b6b", "Pending Applications", s.pending_applications],
          [Radio, "#d4b876", "Official Items", s.official_items_posted],
          [Activity, "#FFFFFF", "Total Consultations", s.total_consultations],
          [FileText, "#8C8C8C", "Community Threads", s.community_threads],
        ].map(([Icon, color, label, val]) => (
          <div key={label} className="panel p-4 corner-tick">
            <Icon size={16} style={{color}} />
            <div className="font-display text-3xl mt-2" style={{color}}>{val}</div>
            <div className="label-mono mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Per-leader ledger */}
      <h2 className="font-display text-3xl mt-16">Leadership activity</h2>
      <div className="mt-6 overflow-x-auto border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3 text-right">Portfolio Items</th>
              <th className="px-4 py-3 text-right">Letters</th>
              <th className="px-4 py-3 text-right">Projects</th>
              <th className="px-4 py-3 text-right">Comms</th>
              <th className="px-4 py-3 text-right">Official Polls</th>
              <th className="px-4 py-3 text-right">Threads</th>
              <th className="px-4 py-3">Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {data.ledger.map((row, i) => (
              <tr key={i} onClick={()=>nav(`/portal/portfolios?owner=${row.id}`)} className="border-t border-white/10 hover:bg-[#89b4fa]/5 cursor-pointer group" data-testid={`ledger-row-${i}`}>
                <td className="px-4 py-3 font-mono text-[11px] text-[#89b4fa] uppercase tracking-widest">{ROLE_LABEL[row.role]}</td>
                <td className="px-4 py-3 font-display text-base group-hover:text-[#89b4fa]">
                  {row.name}
                  <span className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] text-white/40">→ portfolio</span>
                </td>
                <td className="px-4 py-3 text-right font-mono">{row.portfolio_items}</td>
                <td className="px-4 py-3 text-right font-mono text-[#d4b876]">{row.letters}</td>
                <td className="px-4 py-3 text-right font-mono">{row.projects}</td>
                <td className="px-4 py-3 text-right font-mono">{row.communications}</td>
                <td className="px-4 py-3 text-right font-mono text-[#ef5b6b]">{row.official_polls}</td>
                <td className="px-4 py-3 text-right font-mono">{row.threads_opened}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-white/50">
                  {row.last_activity ? new Date(row.last_activity).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 border border-white/10 p-4 font-mono text-[11px] text-white/50 leading-relaxed">
        <div className="label-mono text-[#d4b876] mb-2">DATA INTEGRITY NOTICE</div>
        This ledger displays aggregate counts only. Document bodies, ballot cast lists, and personal information are never exposed. All data respects OCAP® principles (Ownership, Control, Access, Possession) of the First Nations Information Governance Centre.
      </div>
    </div>
  );
}
